import { useRealm } from '@/composables/useRealm'
import { getRealmId } from '@/utils/realmId'
import { defineStore } from 'pinia'
import { Ref, computed, isRef, ref, toRefs, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAccessStore } from './access'
import {
  Feature,
  getFeatureFromPath,
  getFeaturePath,
  mutateFeature,
  reconstructFeaturePath,
  setParents
} from './feature'

const ROOT_NAME = 'home'

export const useFeatures = defineStore('features', () => {
  const realmId = getRealmId()
  const { settings: realmSettings } = toRefs(useRealm())

  const rootFeature = ref<Feature | undefined>()
  // Set once initializeOrgFlowFeatures() (HomePage.vue) applies an org-scoped tree via
  // setOrgFlowRootFeature, so the realmSettings watcher below stops clobbering it with the
  // unfiltered realm tree if the realm-settings fetch resolves afterward.
  const orgFlowFeaturesApplied = ref(false)

  const accessStore = useAccessStore()
  const featureAccess = computed(() => accessStore.featureAccess)

  const featurePath = ref<string[]>([ROOT_NAME])

  const router = useRouter()
  const route = useRoute()

  const path = computed(() => route.path)

  const mainFeatures = computed(() => rootFeature.value?.children ?? [])

  const mainFeatureName = computed(() => featurePath.value[1])

  const activeFeature = computed<Feature | undefined>(() =>
    rootFeature.value ? getFeatureFromPath(rootFeature.value, featurePath.value) : undefined
  )

  const activeFeatures = computed(() => {
    if (!rootFeature.value) return []
    const feature = getFeatureFromPath(rootFeature.value, featurePath.value)
    return feature ? getFeaturePath(feature) : []
  })

  const setRootFeature = (root: Feature) => {
    if (!realmId) return
    setParents(root)
    rootFeature.value = root
  }

  const setOrgFlowRootFeature = (root: Feature) => {
    orgFlowFeaturesApplied.value = true
    setRootFeature(root)
  }

  watch(
    realmSettings,
    (settings) => {
      if (orgFlowFeaturesApplied.value) return
      if (settings?.features && realmId) {
        setRootFeature(settings.features)
      }
    },
    { immediate: true }
  )

  watch(
    [rootFeature, path],
    () => {
      if (!rootFeature.value) return
      const urlPath = route.path.split('/').filter(Boolean)
      const expectedSegments = activeFeatures.value.filter((f) => f.isRoute).map((f) => f.name)
      const actualSegments = urlPath.slice(1) // remove leading 'home'
      if (expectedSegments.join('/') !== actualSegments.join('/')) {
        featurePath.value = reconstructFeaturePath(rootFeature.value, urlPath)
      }
    },
    { immediate: true }
  )

  const updateRoute = async () => {
    const path =
      `/${ROOT_NAME}/` +
      activeFeatures.value
        .filter((f) => f.isRoute)
        .map((f) => f.name)
        .join('/')
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { components: _c, ...queryWithoutComponents } = route.query
    const result = await router.push({
      path,
      replace: true,
      query: queryWithoutComponents
    })
    if (result) {
      const res = await router.replace(result.to)
      if (res) {
        await router.replace(res.to)
      }
    }
  }

  const pushFeature = (feature: string) => {
    featurePath.value = [...featurePath.value, feature]
    updateRoute()
  }
  const pushFeatures = (features: string[]) => {
    featurePath.value = [...featurePath.value, ...features]
    updateRoute()
  }

  const setFeature = (path: string | Feature) => {
    if (typeof path === 'object') {
      const p = getFeaturePath(path)
        .map((f) => f.name)
        .slice(1)
      featurePath.value = [ROOT_NAME, ...p]
    } else {
      featurePath.value = path.split('/').filter(Boolean)
    }
    updateRoute()
  }

  const popFeature = () => {
    if (featurePath.value.length === 1) return
    featurePath.value.pop()
    updateRoute()
  }

  const replaceFeature = (feature: string) => {
    featurePath.value = [...featurePath.value.slice(0, -1), feature]
    updateRoute()
  }

  const setMainFeature = (feature: string) => {
    if (rootFeature.value === undefined) return
    featurePath.value = [ROOT_NAME, feature]
    updateRoute()
  }

  const resetMainFeature = () => {
    featurePath.value = [ROOT_NAME]
    updateRoute()
  }

  function mutateRootFeature(path: string[], mutation: (feature: Feature) => Feature) {
    if (!rootFeature.value || !realmId) return
    const mutatedFeature = mutateFeature(
      rootFeature.value,
      path,
      realmId,
      featureAccess.value,
      mutation
    )
    setRootFeature(mutatedFeature)
  }

  const hasFeatureAccess = (feature: Feature) => {
    if (!feature.roleAccess) return true
    return feature.roleAccess.some((access) => accessStore.has(access))
  }

  return {
    rootFeature,
    mainFeatures,
    activeFeature,
    featurePath,
    mainFeatureName,
    setRootFeature,
    setOrgFlowRootFeature,
    pushFeature,
    popFeature,
    setFeature,
    hasFeatureAccess,
    setMainFeature,
    resetMainFeature,
    getFeatureFromPath,
    mutateRootFeature,
    replaceFeature,
    pushFeatures
  }
})

export const useFeature = (path?: string | Ref<string | undefined>) => {
  const selectionPath = computed(
    () => (path && isRef(path) ? path.value : path)?.split('/').filter(Boolean) as string[]
  )
  const features = useFeatures()
  const { getFeatureFromPath } = features
  const { rootFeature, featurePath, activeFeature } = toRefs(features)
  const feature = computed<Feature | undefined>(() =>
    selectionPath.value && rootFeature?.value
      ? getFeatureFromPath(rootFeature.value, selectionPath.value, featurePath.value)
      : (activeFeature?.value ?? undefined)
  )

  return {
    feature,
    featurePath
  }
}

export default useFeatures
